/**
 * The lineage rule — ADR-032 §3, as code. ONE place decides what a Document
 * carries into its next lineage, and the carry table in that ADR is this
 * file's contract; `lineage.test.ts` is the table as a machine check.
 *
 * WHY THIS EXISTS. The load door used to compose the next Document by
 * spreading the previous one and then deleting, slice by slice, what it
 * remembered could not survive a change of character. What it did not
 * remember rode along: the boot character's zone into the tiger's Document
 * (ADR-031 T1's first field read), the previous subject's profile onto a
 * four-fingered model (the 2026-08-26 dancingSquirrel refusal), and today's
 * `recenters` / `chestRepair`, which name bones of a rig that is gone. Five
 * apply-time guards stood in for one unstated rule.
 *
 * A NEW TARGET BEGINS A NEW LINEAGE. Target-bound state resets: every edit
 * (poses, arm-space, head-space, material factors), authored clips (their
 * keys name bones), zones (cut on a mesh), hands and confirmations, recenters
 * and the chest-repair opt-out, the profile (the floor — the run upgrades it)
 * and the family (`unknown` — detection decides). The room carries: fps, the
 * sources already loaded, and the clip selection (re-validated against the
 * new target by the run — never silently dropped).
 *
 * A NEW SOURCE CHANGES NOTHING ABOUT THE TARGET. Everything carries. TODAY
 * THE ROOM HOLDS ONE SOURCE: the load door has always replaced the source
 * list and the selection with the newest drop (one animation on stage at a
 * time), and this function keeps that contract rather than widening it in a
 * refactor — ADR-032 §3's "upsert by label" is the same behaviour on a list
 * of one. A multi-source room is a separate decision with its own evidence.
 *
 * COMPOSES AND VALIDATES: `parseDocument` begins a lineage, and ADR-007 §4
 * gives that to the load door alone — which is why this lives beside it in
 * the kernel and the app only calls it.
 *
 * Depends on: document (parseDocument, types). Depended on by: the app's
 * importModel / importClip (the two gestures' one door).
 */
import { parseDocument, type AnimatorDocument, type SpecimenRef } from './document.js';

/** What a new TARGET arrives with — the file's identity, and what the run will measure from. */
export interface NextTarget {
  /** The Document's work title, re-derived from the file by the caller. */
  readonly name: string;
  readonly label: string;
  readonly specimen: SpecimenRef;
  /** The profile FLOOR; the run upgrades when the rig earns it (the dancingSquirrel lesson). */
  readonly profile: string;
  /** Absent = `unknown`: honest ignorance, no other family's priors applied. */
  readonly family?: string;
  readonly selectSkeleton?: string;
  /** FACE FORWARD: the turn the load door measured on this new body (absent = faces +Z as authored). */
  readonly facing?: { readonly turnDeg: number; readonly reason: string };
}

/** What a new SOURCE arrives with. */
export interface NextSource {
  readonly label: string;
  readonly specimen: SpecimenRef;
  readonly selectSkeleton?: string;
  /** The take chosen BY NAME before the Document is written (§8). */
  readonly take: string;
}

/** The carry table's left column and right column, in one place: a new target keeps the room and resets the body. */
export function beginTargetLineage(doc: AnimatorDocument, next: NextTarget): AnimatorDocument {
  return parseDocument({
    schemaVersion: doc.schemaVersion,
    name: next.name,
    fps: doc.fps,
    // The room carries: sources and the selection (the run re-validates the
    // selection against the new target and refuses, by name, what does not fit)
    // — but NOT the previous target's PROFILE. A selection's `profile` is what
    // the retarget RUNS at, so a five-fingered rig's `minimal+hands`, carried
    // onto a four-fingered one, refuses at `LeftHandRing1` and the load door
    // reports "could not be imported" for a body that is perfectly fine on its
    // own (measured 2026-09-07 across the whole FingerArmed bench: 7 of 7
    // four-digit bodies refused after a five-digit one, 0 of 63 five-digit
    // bodies refused; Allen: "I have to reset if loading a non-finger app over
    // an n-finger app"). `target.profile` was cured for exactly this in the
    // dancingSquirrel refusal; the selection's copy is the door that cure
    // missed. Both start at the new target's FLOOR — the run upgrades when
    // THIS rig earns it.
    sources: doc.sources,
    clipSelections: doc.clipSelections.map((s) => ({ ...s, profile: next.profile })),
    target: {
      label: next.label,
      specimen: next.specimen,
      family: next.family ?? 'unknown',
      profile: next.profile,
      ...(next.selectSkeleton !== undefined ? { selectSkeleton: next.selectSkeleton } : {}),
      // FACE FORWARD: the load door's measured turn, declared on the NEW body (never carried).
      ...(next.facing !== undefined ? { facing: next.facing } : {}),
      // Hand work is the next move on THIS rig. Empty is "none declared", never "none present".
      hands: [],
      confirmations: [],
      // recenters / chestRepair: omitted — they named bones of the previous rig (ADR-032 D3).
      // tails: omitted — a tail is declared on a ZONE, and zones are cut on a mesh (ADR-031 T2).
      // softTissue: omitted — a leaf is measured on THIS body (or a zone cut on its mesh) (ADR-038 J2).
      // hair: omitted — its region is a zone cut on a mesh, its skull read on this head (ADR-039).
    },
    // Authored keys and pose edits name bones this skeleton may not have; every
    // other edit is a per-character factor; zones are cut on a mesh.
    clips: [],
    edits: { poses: [] },
    zones: [],
  });
}

/** A new source onto the same target: the room's one source and its selection are replaced; the body is untouched. */
export function addSourceToLineage(doc: AnimatorDocument, source: NextSource): AnimatorDocument {
  const { take, ...session } = source;
  return parseDocument({
    ...doc,
    sources: [session],
    clipSelections: [{ source: source.label, take, profile: doc.target.profile }],
  });
}
